import { useEffect, useRef } from "react";
import debounce from "lodash/debounce";

/**
 * Persist a JSON-serializable value to sessionStorage on change, debounced.
 * Stable debounce instance survives renders so rapid value changes coalesce
 * into a single write (avoids the "deps include value -> recreate debounce
 * every render -> never fires" pitfall).
 * @param {string} key
 * @param {unknown} value
 * @param {number} debounceMs
 */
export default function useSessionStorageSync(key, value, debounceMs = 300) {
    const valueRef = useRef(value);
    valueRef.current = value;
    const keyRef = useRef(key);
    keyRef.current = key;

    const debouncedSaveRef = useRef(null);
    if (debouncedSaveRef.current == null) {
        debouncedSaveRef.current = debounce(() => {
            try {
                sessionStorage.setItem(
                    keyRef.current,
                    JSON.stringify(valueRef.current)
                );
            } catch {
                // ignore quota / serialization errors silently
            }
        }, debounceMs);
    }

    useEffect(() => {
        debouncedSaveRef.current();
    }, [value]);

    useEffect(() => {
        return () => debouncedSaveRef.current?.cancel();
    }, []);
}

export function readSessionStorage(key, fallback) {
    try {
        const raw = sessionStorage.getItem(key);
        return raw == null ? fallback : JSON.parse(raw);
    } catch {
        return fallback;
    }
}
